'use client';

import type { FC, ReactNode } from 'react';
import type { BadgeOptions } from '@/app/components/shared/CustomRadioGroup';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { cn } from '@/app/utils';
export interface SelectOption {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  badge?: BadgeOptions;
  id?: string;
  tooltip?: string | ReactNode;
  disabled?: boolean;
  isVipOption?: boolean;
  isHidden?: () => boolean;
  onItemClick?: () => void;
  desc?: string;
}

export interface CommonSelectProps {
  value: string;
  options: SelectOption[];
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  isNewSetting?: boolean;
  disabledTip?: string;
  icon?: ReactNode;
  contentClassName?: string;
  itemClassName?: string;
  hideArrow?: boolean;
  side?: 'top' | 'bottom' | 'left' | 'right';
  renderValue?: (value: string) => ReactNode;
  innerItemClassName?: string;
  align?: 'start' | 'center' | 'end';
  customTooltipVariant?: 'basic' | 'extend' | 'glass';
  /** 是否启用展开动效 */
  enableAnimation?: boolean;
  selectGroupClassName?: string;
}

/**
 * 通用 Select 组件
 * 封装了项目中常用的 Select 样式和结构
 */
export const CommonSelect: FC<CommonSelectProps> = ({
  value,
  options,
  onValueChange,
  disabled = false,
  placeholder,
  className,
  isNewSetting = false,
  disabledTip,
  icon,
  contentClassName,
  itemClassName,
  hideArrow = false,
  side,
  renderValue,
  innerItemClassName,
  align = 'start',
  customTooltipVariant,
  enableAnimation = false,
  selectGroupClassName,
}) => {
  const currentOption = options.filter((option) => option.value === value)[0];

  // 确定要显示的 tooltip 内容
  const tooltipContent = disabled ? disabledTip : currentOption?.tooltip;

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <CustomTooltip
        content={tooltipContent}
        side={'right'}
        sideOffset={4}
        disableHoverableContent={true}
        variant={customTooltipVariant}
      >
        <SelectTrigger
          className={cn(
            'relative flex h-10 w-full items-center rounded-10 border-none bg-system-white04 p-2 text-system-white hover:border-system-white64 [&>span]:overflow-visible',
            className,
          )}
          hideArrow={hideArrow}
        >
          {renderValue ? (
            renderValue(value)
          ) : (
            <>
              {icon}
              <SelectValue placeholder={placeholder} />
            </>
          )}
        </SelectTrigger>
      </CustomTooltip>
      <SelectContent
        className={cn(
          'w-[var(--radix-select-trigger-width)] rounded-8 border border-system-white02 border-solid bg-system-bg02 p-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.32)]',
          enableAnimation &&
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=open]:animate-in',
          contentClassName,
        )}
        side={side}
        align={align}
      >
        <SelectGroup className={cn('flex flex-col gap-1', selectGroupClassName)}>
          {options.map((option, index) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className={cn(
                'relative flex h-10 items-center p-1 [&>span]:w-full [&>span]:overflow-visible',
                itemClassName,
              )}
              disabled={option.disabled}
              onPointerUp={option.onItemClick}
            >
              <CustomTooltip
                content={option.tooltip}
                side="right"
                sideOffset={4}
                disableHoverableContent={true}
                variant={customTooltipVariant}
              >
                <div className="flex w-full items-center justify-between">
                  <div className={cn('flex w-full items-center p-1', innerItemClassName)}>
                    {option.icon}

                    <div className="space-y-1">
                      <div className="flex items-center">
                        <span>
                          {option.label}
                          {index === 0 && isNewSetting && (
                            <span className="ml-1 rounded-4 bg-system-blue01 px-[5px] py-[1px] font-medium text-system-white text-xs">
                              NEW
                            </span>
                          )}
                        </span>
                        {option.badge ? option.badge.content : null}
                      </div>
                      {option.desc ? (
                        <CustomTooltip
                          content={option.desc}
                          showOnlyOnOverflow={true}
                          maxLines={1}
                          variant={customTooltipVariant}
                        >
                          <div className="text-system-white48 text-xs">{option.desc}</div>
                        </CustomTooltip>
                      ) : null}
                    </div>
                  </div>
                </div>
              </CustomTooltip>
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
